const DATE_FILTER_FIELDS = new Set(["createDate", "crawlDate", "exportDate", "failureDate"])

const NUMERIC_FIELDS = new Set([
    "id",
    "price",
    "lastPrice",
    "shopPrice",
    "shopTotalPrice",
    "coupon",
    "priceDiff",
    "bruttoFaktor",
    "nettoFaktor",
    "productRanking",
    "istNettoFaktor",
    "ekPrice",
])

const EXACT_MATCH_FIELDS = new Set([
    "ean",
])

const PREFIX_MATCH_FIELDS = new Set([
    "fromProductnr",
    "toProductnr",
    "productnr",
])

const UI_TO_API_FIELD = {
    rule: "rule.name",
    "rule.name": "rule.name",
    ppCurrency: "ppCurrency.currency",
    "ppCurrency.currency": "ppCurrency.currency",
    ppShop: "ppShop.name",
    "ppShop.name": "ppShop.name",
    failedRule: "failedRule.name",
    "failedRule.name": "failedRule.name",
    approvedByUser: "approvedByUser.name",
    confirmedBy: "approvedByUser.name",
    creationDate: "createDate",
    errorReason: "failureReason",
    "rule.description": "rule.name",
}

export function isDateFilterField(field) {
    return DATE_FILTER_FIELDS.has(field) || field === "createDateFormatted"
}

export function getDateFilterApiField(uiField) {
    if (uiField === "createDateFormatted") {
        return "createDate"
    }
    return UI_TO_API_FIELD[uiField] || uiField
}

function formatDateForLikeFilter(value) {
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return null
        }
        const y = value.getFullYear()
        const m = String(value.getMonth() + 1).padStart(2, "0")
        const day = String(value.getDate()).padStart(2, "0")
        return `${y}-${m}-${day}`
    }

    const str = String(value).trim()
    if (!str) {
        return null
    }

    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`
    }

    const deMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})/)
    if (deMatch) {
        const day = deMatch[1].padStart(2, "0")
        const month = deMatch[2].padStart(2, "0")
        let year = deMatch[3]
        if (year.length === 2) {
            year = `20${year}`
        }
        return `${year}-${month}-${day}`
    }

    const parsed = new Date(str)
    if (!Number.isNaN(parsed.getTime())) {
        const y = parsed.getFullYear()
        const m = String(parsed.getMonth() + 1).padStart(2, "0")
        const day = String(parsed.getDate()).padStart(2, "0")
        return `${y}-${m}-${day}`
    }

    return str
}

function appendDateDayFilter(result, apiField, value) {
    const likeValue = formatDateForLikeFilter(value)
    if (likeValue) {
        result[`${apiField}:LIKE`] = likeValue
    }
}

export function mapSortField(uiField) {
    if (!uiField || uiField.startsWith("spacer")) {
        return null
    }
    return UI_TO_API_FIELD[uiField] || uiField
}

function appendUiFilter(result, uiField, filterObj) {
    const value = filterObj?.value
    if (value === undefined || value === null) {
        return
    }
    const isDateValue = value instanceof Date
    const isTextOrNumber = typeof value === "string" || typeof value === "number"
    if (!isDateValue && !isTextOrNumber) {
        return
    }
    if (uiField.startsWith("spacer") || uiField === "-" || uiField === "--") {
        return
    }

    const apiField = UI_TO_API_FIELD[uiField] || uiField

    if (isDateFilterField(uiField)) {
        if (value instanceof Date || (typeof value === "string" && value.trim())) {
            appendDateDayFilter(result, getDateFilterApiField(uiField), value)
        }
        return
    }

    const strVal = String(value).trim()
    if (!strVal) {
        return
    }

    if (NUMERIC_FIELDS.has(apiField)) {
        appendNumericFilter(result, apiField, strVal)
    } else if (EXACT_MATCH_FIELDS.has(apiField)) {
        result[`${apiField}:EQUALS`] = strVal
    } else if (PREFIX_MATCH_FIELDS.has(apiField)) {
        result[`${apiField}:STARTS_WITH`] = strVal
    } else {
        result[`${apiField}:LIKE`] = strVal
    }
}

function appendNumericFilter(result, field, raw) {
    const val = raw.trim().replace(/,/g, ".")
    if (!val) {
        return
    }
    if (!/\d/.test(val)) {
        result[`${field}:EQUALS`] = -1
        return
    }
    if (val.startsWith("<=")) {
        result[`${field}:LESS_OR_EQUAL`] = val.slice(2).trim()
    } else if (val.startsWith(">=")) {
        result[`${field}:GREATER_OR_EQUAL`] = val.slice(2).trim()
    } else if (val.startsWith("<")) {
        result[`${field}:LESS_THAN`] = val.slice(1).trim()
    } else if (val.startsWith(">")) {
        result[`${field}:GREATER_THAN`] = val.slice(1).trim()
    } else if (val.includes("..")) {
        const parts = val.split("..")
        result[`${field}:GREATER_OR_EQUAL`] = parts[0].trim()
        result[`${field}:LESS_OR_EQUAL`] = parts[1].trim()
    } else {
        result[`${field}:EQUALS`] = val
    }
}

export function buildApiFilters(uiFilters, options = {}) {
    const result = { ...(options.extra || {}) }
    Object.entries(uiFilters || {}).forEach(([uiField, filterObj]) => {
        appendUiFilter(result, uiField, filterObj)
    })
    return result
}

export function appendChannelFilter(filters, channelId) {
    const hasChannelId = Number.isInteger(channelId) && channelId >= 0
    if (!hasChannelId) {
        return filters
    }
    filters["channel.id:EQUALS"] = String(channelId)
    return filters
}

export const NUMERIC_FILTER_PLACEHOLDER = ">=10, <5, 1..5"

export function formatSentinelNumber(value) {
    if (value == null || value === "") {
        return "—"
    }
    const n = Number(value)
    if (Number.isFinite(n) && n >= 2147483647) {
        return "—"
    }
    return value
}

export function formatRuleDisplay(rule) {
    if (!rule) {
        return ""
    }
    const id = rule.id != null ? rule.id : ""
    const name = rule.name || ""
    return `${id} ${name}`.trim()
}
